// ============================================================
// A BEAT'S BURST, DRAWN (plan body-map M2; the Auras design page's bursts,
// accents and moments). Inside the one beat an attack plays: a piece per
// round from the landing on - a tracer and a flash for each hit, a dodged
// round whipping past - then, past six, one rattle with its count; the
// weapon's specials as accents in their tones (an element in the wound, a
// holy shaft, a drain); and what the target's nature does - a field's cells
// rising to the shot, a daemonic soak's ink, Warp Instability's backtrack.
// The timing is engine/display/beats; each piece draws itself by its age.
// ============================================================

import {
  ageOf as pieceAge,
  MOMENT_MS,
  PIECE_MS,
  type Beat,
  type BeatAccent,
  type BeatHit,
  type BeatMoment,
} from '../../engine/display/beats';
import { lerpXY } from '../../engine/display/timeline';
import { accentShapeOf, type AccentShape } from '../../engine/display/accents';
import { BACKTRACK } from '../../engine/plate/natures';
import { tripletOf } from '../../engine/plate/triplet';
import { seeded } from '../../engine/plate/hash';
import type { ScreenXY } from '../battlefieldTokens/markerGeometry';
import type { ReactNode } from 'react';
import clsx from '../../utils/clsx';
import styles from '../../styles/Display.module.css';

type XY = ScreenXY;

/** Where a dodged round whips past the target, screen px (the design page's). */
const PAST = { x: 26, y: -14 };
/** A tracer's flight from the muzzle to the target, as a share of the hit's life (120 ms). */
const TRACER_K = 120 / PIECE_MS.hit;
/** The backtrack's shells, flung as far as the plate's (its frame units) at the map's size, screen px. */
const SHELL_PX = 30 / 430;

/** Half through, for a still screen: every piece at once, mid-life, none waiting on a clock. */
const STILL_AGE = 0.5;

/** How far a piece is through its life at `t` (0..1), or null outside it; on a still screen (`t` null), half through. */
const ageOf = (at: number, life: number, t: number | null) => pieceAge(at, life, t, STILL_AGE);

/** The burst's pieces at `t` ms into the beat; `t` null on a still screen, which shows the whole burst at once. */
export default function BeatBurst({ beat, t, from, to }: { beat: Beat; t: number | null; from: XY; to: XY }) {
  const shot = beat.kind === 'shot';
  return (
    <g data-testid="display-beat-burst">
      {beat.hits.map((h, i) => (
        <Round key={i} hit={h} k={ageOf(h.at, PIECE_MS.hit, t)} from={from} to={to} shot={shot} />
      ))}
      {beat.moments.map((m, i) => (
        <Moment key={`m${i}`} moment={m} k={ageOf(m.at, MOMENT_MS[m.kind], t)} from={from} to={to} />
      ))}
      {beat.accents.map((a, i) => (
        <Accent key={`a${i}`} accent={a} k={ageOf(a.at, PIECE_MS.accent, t)} to={to} seed={`${beat.id}:${i}`} />
      ))}
      {beat.rattle && ageOf(beat.rattle.at, PIECE_MS.rattle, t) !== null && (
        <text data-testid="display-beat-rattle" className={styles.beatCount} x={to.x + 14} y={to.y - 10}>
          {`+${beat.rattle.count}`}
        </text>
      )}
    </g>
  );
}

/** One round: a dodged one streaks past; a landed one flies in (a shot) and flashes where it lands. */
function Round({ hit, k, from, to, shot }: { hit: BeatHit; k: number | null; from: XY; to: XY; shot: boolean }) {
  if (k === null) return null;
  if (hit.glance) {
    const s = hit.glance === 'left' ? -1 : 1;
    const past = { x: to.x + PAST.x * s, y: to.y + PAST.y };
    const at = lerpXY(to, past, k);
    return (
      <line
        data-testid="display-beat-hit"
        data-glance={hit.glance}
        className={styles.beatTracer}
        x1={at.x}
        y1={at.y}
        x2={past.x + PAST.x * s * 0.6}
        y2={past.y}
        opacity={1 - k}
      />
    );
  }
  // Never on a still screen: its pieces stand half through their lives, past the flight.
  const flying = shot && k < TRACER_K;
  return (
    <g data-testid="display-beat-hit" data-blocked={hit.blocked || undefined} data-soaked={hit.soaked || undefined}>
      {flying && <line className={styles.beatTracer} x1={from.x} y1={from.y} x2={to.x} y2={to.y} />}
      <circle
        className={clsx(styles.beatHitFlash, hit.blocked && styles.beatFieldFlash, hit.crit && styles.beatCritFlash)}
        cx={to.x}
        cy={to.y}
        r={5 + 22 * k}
        opacity={1 - k}
      />
    </g>
  );
}

/** One moment's drawing, `k` through its life (0..1). */
type MomentDraw = (p: { k: number; from: XY; to: XY }) => ReactNode;

/** What the target's nature does: a field's cells at the shot, a daemonic soak's ink, the backtrack's shells, Undying's fall. */
function Moment({ moment, k, from, to }: { moment: BeatMoment; k: number | null; from: XY; to: XY }) {
  if (k === null) return null;
  const Draw = MOMENTS[moment.kind];
  return <Draw k={k} from={from} to={to} />;
}

/** A field's cells raised toward the shot. */
const Block: MomentDraw = ({ k, from, to }) => {
  const a = Math.atan2(from.y - to.y, from.x - to.x);
  return (
    <g data-testid="display-beat-moment" data-moment="block" opacity={1 - k}>
      {[-0.5, -0.25, 0, 0.25, 0.5].map((d) => (
        <circle
          key={d}
          className={styles.beatFieldCell}
          cx={to.x + Math.cos(a + d) * 16}
          cy={to.y + Math.sin(a + d) * 16}
          r={4}
        />
      ))}
    </g>
  );
};

/** The warp's ink swallowing the wound. */
const Soak: MomentDraw = ({ k, to }) => (
  <ellipse
    data-testid="display-beat-moment"
    data-moment="soak"
    className={styles.beatSoak}
    cx={to.x}
    cy={to.y}
    rx={10 + 14 * k}
    ry={7 + 10 * k}
    opacity={1 - k}
  />
);

/** The backtrack: the plate's four shells flung out, hanging, then rewound into it. */
const Backtrack: MomentDraw = ({ k, to }) => {
  const out = (k < 0.6 ? k / 0.6 : 1 - (k - 0.6) / 0.4) * SHELL_PX;
  return (
    <g data-testid="display-beat-moment" data-moment="backtrack">
      {BACKTRACK.shells.map((s, i) => (
        <circle
          key={i}
          className={styles.beatShell}
          cx={to.x + s.dx * out}
          cy={to.y + s.dy * out}
          r={6}
          opacity={s.opacity * (1 - k)}
        />
      ))}
    </g>
  );
};

/** Undying's fall, s into its life: down in 0.5 s, lying to 1.5 s, the fall run backwards to 2.3 s (the design page). */
const fallAt = (a: number) => {
  if (a < 0.5) return a / 0.5;
  if (a < 1.5) return 1;
  return a < 2.3 ? 1 - (a - 1.5) / 0.8 : 0;
};
/** How tall the fallen figure is, scene units - about a figure's stalk and head (the strike circle is 26). */
const FIGURE_TALL = 46;

/**
 * Undying cheats death: it falls, lies with no life signs, and stands again. Drawn over its marker (which the rules
 * keep standing) as the figure's line pivoting at its feet - `to` is its head - with the head at the end.
 */
const UndyingFall: MomentDraw = ({ k, to }) => {
  const a = k * (MOMENT_MS.undying / 1000);
  const fall = fallAt(a);
  const lying = a > 0.4 && a < 1.6;
  const turn = (fall * Math.PI) / 2;
  const foot = { x: to.x, y: to.y + FIGURE_TALL };
  const head = { x: foot.x + Math.sin(turn) * FIGURE_TALL, y: foot.y - Math.cos(turn) * FIGURE_TALL };
  return (
    <g data-testid="display-beat-moment" data-moment="undying" data-fall={Number(fall.toFixed(2))}>
      <g className={clsx(styles.beatUndying, lying && styles.beatUndyingDead)}>
        <line x1={foot.x} y1={foot.y} x2={head.x} y2={head.y} />
        <circle cx={head.x} cy={head.y} r={7} />
      </g>
      {lying && (
        <text className={styles.beatNoLife} x={foot.x + FIGURE_TALL / 2} y={foot.y - 20}>
          NO LIFE SIGNS
        </text>
      )}
    </g>
  );
};

const MOMENTS: Record<BeatMoment['kind'], MomentDraw> = {
  block: Block,
  soak: Soak,
  backtrack: Backtrack,
  undying: UndyingFall,
};

/** The tone each special plays in on the map (its plate colour family). */
const TONES: Record<string, string> = {
  flame: 'fire',
  tearing: 'fire',
  melta: 'fire',
  volatile: 'fire',
  shock: 'shock',
  corrosive: 'acid',
  toxicw: 'acid',
  sanctified: 'holy',
  blessed: 'holy',
  exorcist: 'holy',
  witchbane: 'holy',
  force: 'psy',
  warp: 'psy',
};

/** A special's accent, in the plate's shape for it (K6: engine/display/accents) and its tone; `seed`: beat and index. */
function Accent({ accent, k, to, seed }: { accent: BeatAccent; k: number | null; to: XY; seed: string }) {
  if (k === null) return null;
  const tone = TONES[accent.key] ?? accent.role;
  const shape = accentShapeOf(accent.key, accent.role);
  const common = {
    'data-testid': 'display-beat-accent',
    'data-accent': accent.key,
    'data-tone': tone,
    'data-shape': shape,
    opacity: 1 - k,
  };
  return (
    <g {...common} className={clsx(SHAPE_CLASS[shape], styles[`tone-${tone}`])}>
      {SHAPES[shape](k, to, seed)}
    </g>
  );
}

/**
 * A shape's geometry at the landing, `k` through its life, `seed` the beat's id and the accent's index (a shape that
 * turns per wound reads it); its wrapper says what it is and wears its tone.
 */
type ShapeDraw = (k: number, to: XY, seed: string) => ReactNode;
/** A path's `d` from points. */
const pathOf = (pts: readonly (readonly [number, number])[]) => 'M ' + pts.map(([x, y]) => `${x} ${y}`).join(' L ');

/** How each shape's strokes are drawn: a filled shaft, rings, lines, or (the drips) the tone alone. */
const SHAPE_CLASS: Record<AccentShape, string | undefined> = {
  shaft: styles.beatAccent,
  point: styles.beatAccentRing,
  flames: styles.beatAccentLines,
  arcs: styles.beatAccentLines,
  drips: undefined,
  tear: styles.beatAccentLines,
  triplet: styles.beatAccentRing,
  rings: styles.beatAccentRing,
  glint: styles.beatAccentLines,
  push: styles.beatAccentRing,
  pull: styles.beatAccentRing,
  mark: styles.beatAccentRing,
};

/** The plate's triplet at the map's scale: its cores ~6 px from the landing (the plate's ~90 frame units). */
const TRIPLET_PX = 6 / 90;
/** A triplet core's life, as a share of the accent's: the last core still runs it out. */
const CORE_LIFE = 0.8;

/** Two rings about the landing, their radii as the shape grows or closes. */
const concentric = (to: XY, radii: readonly number[]) => radii.map((r) => <circle key={r} cx={to.x} cy={to.y} r={r} />);

/** Each shape at a figure's scale: a few strokes, the plate's family. */
const SHAPES: Record<AccentShape, ShapeDraw> = {
  // A holy shaft stands over the wound.
  shaft: (k, to) => <rect x={to.x - 3} y={to.y - 60 * (1 - k * 0.3)} width={6} height={60} />,
  // Melta: a white-hot point, its glow spreading.
  point: (k, to) => (
    <>
      <circle className={styles.beatAccentCore} cx={to.x} cy={to.y} r={3} />
      <circle cx={to.x} cy={to.y} r={6 + 12 * k} />
    </>
  ),
  // Flame: three tongues licking up from the wound.
  flames: (k, to) =>
    [-8, 0, 8].map((dx, i) => (
      <path
        key={dx}
        d={pathOf([
          [to.x + dx, to.y],
          [to.x + dx * 1.4 + (i - 1) * 3, to.y - 10 - 14 * k],
          [to.x + dx * 0.5, to.y - 22 - 18 * k],
        ])}
      />
    )),
  // Shock: fine arcs crawling out from the wound.
  arcs: (k, to) =>
    [0, 2.1, 4.2].map((a) => {
      const r = 10 + 10 * k;
      const tip = [to.x + Math.cos(a) * r, to.y + Math.sin(a) * r] as const;
      const mid = [to.x + Math.cos(a + 0.5) * r * 0.55, to.y + Math.sin(a + 0.5) * r * 0.55] as const;
      return <path key={a} d={pathOf([[to.x, to.y], mid, tip])} />;
    }),
  // Corrosive, Toxic: drips running down from the wound.
  drips: (k, to) =>
    [-9, -3, 3, 9].map((dx, i) => <circle key={dx} cx={to.x + dx} cy={to.y + 4 + (12 + i * 3) * k} r={2.2} />),
  // Tearing, Felling: ragged cuts across the wound.
  tear: (k, to) =>
    [-5, 5].map((dy) => (
      <path
        key={dy}
        d={pathOf([
          [to.x - 14 * (0.5 + k), to.y + dy],
          [to.x - 4, to.y + dy - 3],
          [to.x + 4, to.y + dy + 3],
          [to.x + 14 * (0.5 + k), to.y + dy],
        ])}
      />
    )),
  // Tearing: the plate's hammer triplet, each wound's triangle turned its own way (engine/plate/triplet).
  triplet: (k, to, seed) =>
    tripletOf([to.x, to.y], TRIPLET_PX, seeded(`accent:${seed}`)).map(({ at: [x, y], delay, heat }, i) => {
      const life = (k - (delay * 1000) / PIECE_MS.accent) / CORE_LIFE;
      if (life < 0) return null;
      const kk = Math.min(1, life);
      return (
        <g key={i} data-core={i}>
          <circle className={styles.beatAccentCore} cx={x} cy={y} r={2.6 * heat * (1 - 0.6 * kk)} />
          {/* A ring, never a disc: the tone's fill reaches the group, and the ring is drawn over the core. */}
          <circle cx={x} cy={y} r={2 + 12 * kk} fill="none" />
        </g>
      );
    }),
  // Force, Warp, Witchbane: psychic rings standing out from the body.
  rings: (k, to) => concentric(to, [8 + 16 * k, 4 + 26 * k]),
  // A blade's special: its glint across the wound.
  glint: (k, to) => <line x1={to.x - 18} y1={to.y + 10 - 20 * k} x2={to.x + 18} y2={to.y - 10 - 20 * k} />,
  // Impetus, Concussive: the blow's push, an arc opening on the far side.
  push: (k, to) => {
    const r = 10 + 22 * k;
    return <path d={`M ${to.x + r * 0.5} ${to.y - r} A ${r} ${r} 0 0 1 ${to.x + r * 0.5} ${to.y + r}`} />;
  },
  // Graviton: rings closing in on the body.
  pull: (k, to) => concentric(to, [34 - 24 * k, 24 - 18 * k]),
  // An after-effect (Pinning, Haywire's tail, Crippling...): a mark set over the figure.
  mark: (k, to) => (
    <path
      d={pathOf([
        [to.x - 6, to.y - 30 - 6 * k],
        [to.x, to.y - 24 - 6 * k],
        [to.x + 6, to.y - 30 - 6 * k],
      ])}
    />
  ),
};
